import { useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faXTwitter,
  faFacebook,
  faInstagram,
} from "@fortawesome/free-brands-svg-icons";
import {
  faEnvelope,
  faMessage,
  faMagnifyingGlassPlus,
  faXmark,
  faPlus,
} from "@fortawesome/free-solid-svg-icons";
import { TwitterShareButton, FacebookShareButton } from "react-share";
import { toast } from "react-toastify";
import { sendEmailService } from "../services/FileUploadService";
import { useAxiosInstance } from "../services/Interceptor";

const isMobile = /Android|iPhone|iPad|iPod/i.test(navigator.userAgent);

export function SocialPostModal({
  platform,
  imageUrl,
  postText,
  setPostText,
  onClose,
}) {
  const apiInstance = useAxiosInstance();
  const [zoomOpen, setZoomOpen] = useState(false);
  const [emailModalOpen, setEmailModalOpen] = useState(false);
  const [emails, setEmails] = useState([""]);

  const addEmailField = () => setEmails((prev) => [...prev, ""]);

  const removeEmailField = (index) =>
    setEmails((prev) => prev.filter((_, i) => i !== index));

  const handleEmailChange = (index, value) =>
    setEmails((prev) => prev.map((email, i) => (i === index ? value : email)));

  if (!platform || !imageUrl) return null;

  const handleInstagramFlow = async () => {
    const response = await fetch(imageUrl);
    const blob = await response.blob();
    const blobUrl = URL.createObjectURL(blob);

    const link = document.createElement("a");
    link.href = blobUrl;
    link.download = "comparison.png";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    URL.revokeObjectURL(blobUrl);

    window.open("https://instagram.com", "_blank");

    onClose();
  };

  const handleEmail = () => {
    setEmailModalOpen(true);
  };

  const handleEmailSend = async () => {
    const toEmails = emails
      .map((email) => email.trim())
      .filter((email) => email.length > 0);

    if (toEmails.length === 0) {
      toast.error("Please enter at least one email address.");
      return;
    }

    try {
      await sendEmailService({
        apiInstance,
        toEmails,
        imageUrl,
        content: postText,
        subject: "Athlete Comparison",
      });

      toast.success("Email sent successfully!");
      onClose();
    } catch (error) {
      toast.error("Failed to send email.");
    }
  };

  const handleSMS = () => {
    if (!isMobile) {
      alert("SMS is only available on mobile devices.");
      return;
    }

    window.location.href = `sms:?&body=${encodeURIComponent(
      postText + " " + imageUrl,
    )}`;

    onClose();
  };

  return (
    <>
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[9999]">
        <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl p-6">
          <div className="flex justify-between items-center mb-4">
            <h2 className="font-semibold text-xl">
              Create {platform.toUpperCase()} post
            </h2>
            <button
              onClick={onClose}
              className="text-gray-500 hover:text-gray-800 text-lg"
            >
              ✕
            </button>
          </div>
          <div className="mb-4">
            <span className="block text-xs text-gray-500 mb-1">Post text</span>
            <textarea
              className="w-full border rounded-md p-3 text-sm min-h-[100px]"
              value={postText}
              onChange={(e) => setPostText(e.target.value)}
            />
          </div>

          <div className="mb-4">
            <span className="block text-xs text-gray-500 mb-1">
              Image preview
            </span>

            <div className="relative border rounded-md p-3 flex justify-center bg-gray-50">
              <img
                src={imageUrl}
                alt="Comparison"
                className="max-h-[420px] object-contain cursor-pointer transition hover:opacity-90"
                onClick={() => setZoomOpen(true)}
              />

              <button
                onClick={() => setZoomOpen(true)}
                className="absolute bottom-3 right-3 bg-black/60 text-white p-2 rounded-full hover:bg-black"
              >
                <FontAwesomeIcon icon={faMagnifyingGlassPlus} />
              </button>
            </div>
          </div>

          <div className="flex justify-end gap-3 mt-6">
            <button
              onClick={onClose}
              className="px-4 py-2 text-sm rounded-md border border-gray-300 text-gray-700"
            >
              Cancel
            </button>

            {platform === "x" && (
              <TwitterShareButton
                url={`${process.env.REACT_APP_BASE_URL}share/render/social-network?image=${imageUrl}&content=${postText}`}
                title={postText}
                onShareWindowClose={onClose}
              >
                <div className="px-4 py-2 text-sm rounded-md bg-black text-white flex items-center gap-2">
                  <FontAwesomeIcon icon={faXTwitter} />
                  Share on X
                </div>
              </TwitterShareButton>
            )}

            {platform === "facebook" && (
              <FacebookShareButton
                url={imageUrl}
                quote={postText}
                onShareWindowClose={onClose}
              >
                <div className="px-4 py-2 text-sm rounded-md bg-blue-600 text-white flex items-center gap-2">
                  <FontAwesomeIcon icon={faFacebook} />
                  Share on Facebook
                </div>
              </FacebookShareButton>
            )}

            {platform === "instagram" && (
              <button
                onClick={handleInstagramFlow}
                className="px-4 py-2 text-sm rounded-md bg-pink-500 text-white flex items-center gap-2"
              >
                <FontAwesomeIcon icon={faInstagram} />
                Prepare Instagram post
              </button>
            )}

            {platform === "email" && (
              <button
                onClick={handleEmail}
                className="px-4 py-2 text-sm rounded-md bg-gray-700 text-white flex items-center gap-2"
              >
                <FontAwesomeIcon icon={faEnvelope} />
                Send Email
              </button>
            )}

            {platform === "sms" && (
              <button
                onClick={handleSMS}
                className="px-4 py-2 text-sm rounded-md bg-green-600 text-white flex items-center gap-2"
              >
                <FontAwesomeIcon icon={faMessage} />
                Send SMS
              </button>
            )}
          </div>
          {emailModalOpen && (
            <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[9999]">
              <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl p-6">
                <div className="flex justify-between items-center mb-4">
                  <h2 className="font-semibold text-xl">Send Email</h2>
                  <button
                    onClick={() => setEmailModalOpen(false)}
                    className="text-gray-500 hover:text-gray-800 text-lg"
                  >
                    <FontAwesomeIcon icon={faXmark} />
                  </button>
                </div>

                <div className="space-y-3">
                  {emails.map((value, index) => (
                    <div key={index} className="flex items-center gap-2">
                      <input
                        type="email"
                        value={value}
                        onChange={(e) =>
                          handleEmailChange(index, e.target.value)
                        }
                        className="flex-1 border rounded-md p-3 text-sm"
                        placeholder="email@example.com"
                      />

                      {index > 0 && (
                        <button
                          onClick={() => removeEmailField(index)}
                          className="p-1 bg-red-500 text-white rounded-md"
                        >
                          <FontAwesomeIcon icon={faXmark} />
                        </button>
                      )}
                    </div>
                  ))}

                  <button
                    onClick={addEmailField}
                    className="p-2 bg-blue-700 text-white rounded-md w-fit flex items-center gap-2"
                  >
                    <span>Add Email</span>
                    <FontAwesomeIcon icon={faPlus} />
                  </button>
                </div>

                <div className="flex justify-end gap-3 mt-6">
                  <button
                    onClick={() => setEmailModalOpen(false)}
                    className="px-4 py-2 text-sm rounded-md border border-gray-300 text-gray-700"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleEmailSend}
                    className="px-4 py-2 text-sm rounded-md bg-gray-700 text-white flex items-center gap-2"
                  >
                    Send
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* ZOOM MODAL */}
      {zoomOpen && (
        <div
          className="fixed inset-0 bg-black/80 flex items-center justify-center z-[10000]"
          onClick={() => setZoomOpen(false)}
        >
          <button className="absolute top-4 right-4 text-white text-3xl">
            <FontAwesomeIcon icon={faXmark} />
          </button>

          <img
            src={imageUrl}
            alt="Zoomed"
            className="max-h-[90vh] max-w-[90vw] object-contain"
          />
        </div>
      )}
    </>
  );
}
